import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaUsers, FaMapMarkedAlt } from 'react-icons/fa';
import { useUser } from '../contexts/UserContext';
import ProfileView from '../components/Profile/ProfileView';
import ProfileList from '../components/Profile/ProfileList';
import '../styles/ProfilePage.css';

/**
 * Main profile page component that handles displaying either a specific profile
 * or a list of community members
 */
const ProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { currentUser, getUserById, isLoggedIn } = useUser();
  
  const [activeTab, setActiveTab] = useState('members');
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  
  // Check if user needs to be logged in
  useEffect(() => {
    // Handle isLoggedIn whether it's a function or a boolean
    const userLoggedIn = typeof isLoggedIn === 'function' ? isLoggedIn() : isLoggedIn;
    if (!userLoggedIn) {
      setShowLoginPrompt(true);
    }
  }, [isLoggedIn]);
  
  // Handle back navigation
  const handleBack = () => {
    navigate(-1);
  };
  
  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };
  
  // Handle login button click
  const handleLoginClick = () => {
    navigate('/login', { state: { returnTo: `/profiles${userId ? `/${userId}` : ''}` } });
  };
  
  // Handle view specific profile
  const handleViewProfile = (profileId) => {
    navigate(`/profiles/${profileId}`);
  };
  
  // If viewing a specific profile
  if (userId) {
    const user = getUserById(userId);
    
    if (!user) {
      return (
        <div className="profile-page profile-page--not-found">
          <div className="profile-page__header">
            <button className="profile-page__back-btn" onClick={handleBack}>
              <FaArrowLeft /> Back
            </button>
            <h1 className="profile-page__title">Profile Not Found</h1>
          </div>
          <div className="profile-page__not-found">
            <p>The user profile you're looking for doesn't exist or has been removed.</p>
            <button 
              className="profile-page__action-btn"
              onClick={() => navigate('/profiles')}
            >
              View All Members
            </button>
          </div>
        </div>
      );
    }
    
    return (
      <div className="profile-page profile-page--single">
        <div className="profile-page__header">
          <button className="profile-page__back-btn" onClick={handleBack}>
            <FaArrowLeft /> Back
          </button>
          <h1 className="profile-page__title">User Profile</h1>
        </div>
        
        <div className="profile-page__content">
          <ProfileView 
            userId={userId} 
            onClose={() => navigate('/profiles')} 
          />
        </div>
      </div>
    );
  }
  
  // If showing the community members list
  return (
    <div className="profile-page profile-page--list">
      <div className="profile-page__header">
        <h1 className="profile-page__title">Community</h1>
      </div>
      
      {showLoginPrompt && !(typeof isLoggedIn === 'function' ? isLoggedIn() : isLoggedIn) ? (
        <div className="profile-page__login-prompt">
          <div className="profile-page__login-content">
            <h2 className="profile-page__login-title">Join Our Community</h2>
            <p className="profile-page__login-text">
              Sign in or create an account to connect with LGBTQ+ travelers and locals across the Caribbean.
            </p>
            <button 
              className="profile-page__login-btn"
              onClick={handleLoginClick}
            >
              Sign In / Sign Up
            </button>
            <button 
              className="profile-page__login-skip"
              onClick={() => setShowLoginPrompt(false)}
            >
              Continue as Guest
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="profile-page__tabs">
            <button 
              className={`profile-page__tab ${activeTab === 'members' ? 'profile-page__tab--active' : ''}`}
              onClick={() => handleTabChange('members')}
            >
              <FaUsers /> Community Members
            </button>
           </div>
          
          <div className="profile-page__content">
            {activeTab === 'members' ? (
              <ProfileList 
                title="Community Members"
                showFilters={true}
                onViewProfile={handleViewProfile}
              />
            ) : (
              <ProfileList 
                title="Local Queer Guides"
                showFilters={true}
                initialFilters={{ onlyLocalQueers: true }}
                onViewProfile={handleViewProfile}
              />
            )}
          </div>
        </>
      )}
      
      {currentUser && (
        <div className="profile-page__current-user">
          <p>
            Signed in as <strong>{currentUser.name}</strong>
          </p>
          <button 
            className="profile-page__my-profile-btn"
            onClick={() => navigate(`/community/account`)}
          >
            View My Profile
          </button>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
